import { useState, useEffect, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch } from "react-redux";
import { fetchLabShiftHeatmap } from "../../../API_Service/Dashboard_Slice";
import { companyID } from "../../../Common/UIComponents";
import html2canvas from "html2canvas";
import { useDashboardSignalR } from "../../..//Common/useDashboardSignalR";

// ─── Constants ────────────────────────────────────────────────────────────────
const COMPANY_ID = companyID;
const PAGE_SIZE = 10;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Exact colors sampled from the reference dashboard design
const COLOR_RANGES = [
  { from: 0, to: 0, color: "#EDEDF2", name: "Empty (0%)" },
  { from: 1, to: 40, color: "#E1EFB8", name: "Available (1%–40%)" },
  { from: 41, to: 70, color: "#FEE89E", name: "Busy (41%–70%)" },
  { from: 71, to: 100, color: "#FC837D", name: "Fully Occupied (71%–100%)" },
  { from: 101, to: 100000, color: "#B88EDF", name: "Overbooked (>100%)" },
];

// Dark navy text reads cleanly on all of the pastel cell backgrounds above
const CELL_TEXT_COLOR = "#33344B";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractShifts(data) {
  const map = new Map();
  data.forEach((r) => {
    if (!map.has(r.ShiftNo)) map.set(r.ShiftNo, r.ShiftName.trim());
  });
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([no, name]) => ({ no, name }));
}

function extractLabs(data) {
  const map = new Map();
  data.forEach((r) => {
    if (!map.has(r.FacilityId)) map.set(r.FacilityId, r.FacilityName);
  });
  return [...map.entries()].map(([id, name]) => ({ id, name }));
}

// series = one per SHIFT (x-axis columns), y = lab names (rows)
function buildSeries(data, shifts, labs) {
  return labs.map((lab) => ({
    name: lab.name,
    data: shifts.map((shift) => {
      const row = data.find(
        (r) => r.FacilityId === lab.id && r.ShiftNo === shift.no,
      );
      return {
        x: shift.name,
        // No cap at 100 anymore — values above 100 are real "Overbooked" cells
        // and need to fall through to the Overbooked color range.
        y: row?.UtilizationPct ?? 0,
        meta: row ?? null,
      };
    }),
  }));
}

function computeStats(data) {
  const total = data.length;
  const avg = total
    ? Math.round(data.reduce((s, r) => s + (r.UtilizationPct ?? 0), 0) / total)
    : 0;
  const high = data.filter((r) => (r.UtilizationPct ?? 0) >= 80).length;
  const low = data.filter(
    (r) => (r.UtilizationPct ?? 0) > 0 && r.UtilizationPct < 50,
  ).length;
  const noData = data.filter(
    (r) => !r.UtilizationPct || r.UtilizationPct === 0,
  ).length;
  return { avg, high, low, noData };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Legend() {
  return (
    <div
      style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}
    >
      {COLOR_RANGES.map((r) => (
        <div
          key={r.name}
          style={{ display: "flex", alignItems: "center", gap: 5 }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: r.color,
              border: r.from === 0 ? "1px solid #D8D8DE" : "none",
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 11, color: "#5F5E5A" }}>{r.name}</span>
        </div>
      ))}
    </div>
  );
}

function StatCards({ stats }) {
  const cards = [
    {
      label: "Avg utilization",
      value: `${stats.avg}%`,
      sub: "across all shifts",
      icon: "trending_up",
      accent: "#0F6E56",
      iconBg: "#E3F3EC",
      cardBg: "#F7FCFA",
      border: "#DFF0E7",
    },
    {
      label: "High utilization",
      value: stats.high,
      sub: "cells ≥ 80%",
      icon: "north_east",
      accent: "#185FA5",
      iconBg: "#E5EFFA",
      cardBg: "#F7FAFE",
      border: "#DEEAF7",
    },
    {
      label: "Low utilization",
      value: stats.low,
      sub: "cells below 50%",
      icon: "south_east",
      accent: "#A32D2D",
      iconBg: "#FBE7E7",
      cardBg: "#FDF7F7",
      border: "#F6DEDE",
    },
    {
      label: "No data cells",
      value: stats.noData,
      sub: "lab × shift combos",
      icon: "help_outline",
      accent: "#BA7517",
      iconBg: "#FCEFD8",
      cardBg: "#FEFAF3",
      border: "#F7E9CB",
    },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 10,
        marginTop: 16,
        marginBottom: 4,
      }}
    >
      {cards.map((c) => (
        <div
          key={c.label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            background: c.cardBg,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: "12px 14px",
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: "#8B8D98", marginBottom: 3 }}>
              {c.label}
            </div>
            <div
              style={{
                fontSize: 22,
                lineHeight: 1,
                fontWeight: 700,
                color: c.accent,
              }}
            >
              {c.value}
            </div>
            <div style={{ fontSize: 11, color: "#A6A8B3", marginTop: 4 }}>
              {c.sub}
            </div>
          </div>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: c.iconBg,
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18, color: c.accent }}
            >
              {c.icon}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LabShiftHeatmap() {
  const today = new Date();
  const dispatch = useDispatch();

  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const chartRef = useRef(null);

  const years = Array.from(
    { length: 4 },
    (_, i) => today.getFullYear() - 2 + i,
  );

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const loadHeatmapData = useCallback(() => {
    setLoading(true);
    setError(null);

    dispatch(fetchLabShiftHeatmap({ companyId: COMPANY_ID, month, year }))
      .unwrap()
      .then((data) => setAllData(Array.isArray(data) ? data : []))
      .catch((err) => setError(err?.message || "Failed to load heatmap data."))
      .finally(() => setLoading(false));
  }, [dispatch, month, year]);

  useEffect(() => {
    setPage(0);
    setSelectedLabId(null);
  }, [month, year]);

  useEffect(() => {
    loadHeatmapData();
  }, [loadHeatmapData]);

  useDashboardSignalR(loadHeatmapData);

  // ── Derived ────────────────────────────────────────────────────────────────
  const shifts = extractShifts(allData);
  const allLabs = extractLabs(allData);
  const totalLabs = allLabs.length;
  const totalPages = Math.ceil(totalLabs / PAGE_SIZE);

  // Which labs to show — either single selected or current page slice
  const visibleLabs = selectedLabId
    ? allLabs.filter((l) => l.id === selectedLabId)
    : allLabs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  // Filter data to only visible labs
  const visibleData = allData.filter((r) =>
    visibleLabs.some((l) => l.id === r.FacilityId),
  );

  const series = buildSeries(visibleData, shifts, visibleLabs);
  const stats = computeStats(allData); // always full dataset

  const ROW_HEIGHT = 38;
  const chartHeight = Math.max(200, visibleLabs.length * ROW_HEIGHT + 80);

  const downloadPNG = async () => {
    const canvas = await html2canvas(chartRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
    });

    const link = document.createElement("a");
    link.download = `LabShiftHeatmap_${month}_${year}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // ── Chart options ──────────────────────────────────────────────────────────
  const chartOptions = {
    chart: {
      type: "heatmap",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            filename: `LabShiftHeatmap_${month}_${year}`,
          },
          svg: {
            filename: `LabShiftHeatmap_${month}_${year}`,
          },
          png: {
            filename: `LabShiftHeatmap_${month}_${year}`,
          },
        },
      },
      animations: { enabled: false },
      background: "transparent",
      fontFamily: "inherit",
    },
    plotOptions: {
      heatmap: {
        radius: 3,
        enableShades: false,
        colorScale: { ranges: COLOR_RANGES },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (_val, { seriesIndex, dataPointIndex, w }) => {
        const meta = w.config.series[seriesIndex]?.data[dataPointIndex]?.meta;
        // Only show "-" when the API returned no row at all for this lab/shift.
        // A real 0% reading always renders as "0%".
        if (!meta) return "-";
        const pct = meta.UtilizationPct ?? 0;
        return `${pct}%`;
      },
      style: { fontSize: "11px", fontWeight: "500", colors: [CELL_TEXT_COLOR] },
    },
    xaxis: {
      // x-axis = shift names (3 columns: First/Second/Third Shift)
      type: "category",
      labels: {
        style: { fontSize: "11px", colors: "#5F5E5A" },
        trim: true,
        maxHeight: 60,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      // y-axis = lab names (rows)
      reversed: true, // ← lab order top-to-bottom matches data order
      labels: {
        style: { fontSize: "11px", colors: "#5F5E5A" },
        maxWidth: 160,
      },
    },
    grid: { padding: { top: 0, right: 8, bottom: 0, left: 8 } },
    legend: { show: false },
    tooltip: {
      custom({ seriesIndex, dataPointIndex, w }) {
        const meta = w.config.series[seriesIndex]?.data[dataPointIndex]?.meta;
        if (!meta)
          return `<div style="padding:8px;font-size:12px">No data</div>`;
        const pct = meta.UtilizationPct ?? 0;
        const color =
          pct > 100
            ? "#B88EDF"
            : pct >= 71
              ? "#FC837D"
              : pct >= 41
                ? "#FEE89E"
                : pct >= 1
                  ? "#E1EFB8"
                  : "#EDEDF2";
        return `
        <div style="padding:8px 10px;font-family:inherit;font-size:12px;min-width:180px">
          <div style="font-weight:500;margin-bottom:4px;color:#2C2C2A">
            ${meta.FacilityName} · ${meta.ShiftName.trim()}
          </div>
          <div style="color:${color};font-size:18px;font-weight:500;margin-bottom:6px">
            ${pct}%
          </div>
          <div style="color:#5F5E5A">Actual: <b>${meta.ActualHours}h</b></div>
          <div style="color:#5F5E5A">Capacity: <b>${Number(meta.CapacityHours).toFixed(2)}h</b></div>
        </div>`;
      },
    },
    states: { hover: { filter: { type: "darken", value: 0.85 } } },
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "inherit", padding: "1rem 0" }}>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 500, color: "#2C2C2A" }}>
          Lab Occupancy — Shift View
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={month}
            onChange={(e) => setMonth(+e.target.value)}
            style={SEL}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(+e.target.value)}
            style={SEL}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Legend ── */}
      <Legend />

      {/* ── Stats ── */}
      {!loading && !error && allData.length > 0 && <StatCards stats={stats} />}

      {/* ── Lab selector + pagination ── */}
      {!loading && !error && allLabs.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          {/* Lab dropdown */}
          <select
            value={selectedLabId ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedLabId(val ? +val : null);
              setPage(0);
            }}
            style={{ ...SEL, minWidth: 180 }}
          >
            <option value="">All Labs (paged)</option>
            {allLabs.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>

          {/* Pagination — only when showing all labs */}
          {!selectedLabId && totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                style={pgBtn(page === 0)}
              >
                ‹
              </button>
              <span
                style={{ fontSize: 12, color: "#5F5E5A", padding: "0 4px" }}
              >
                {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, totalLabs)} of {totalLabs}{" "}
                labs
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                style={pgBtn(page === totalPages - 1)}
              >
                ›
              </button>
            </div>
          )}

          {/* Clear single lab filter */}
          {selectedLabId && (
            <button
              onClick={() => setSelectedLabId(null)}
              style={{
                ...pgBtn(false),
                color: "#A32D2D",
                borderColor: "#A32D2D",
              }}
            >
              ✕ Clear
            </button>
          )}

          {/* Shift names info */}
          {shifts.length > 0 && (
            <span
              style={{ fontSize: 11, color: "#888780", marginLeft: "auto" }}
            >
              Columns: {shifts.map((s) => s.name).join(" · ")}
            </span>
          )}
        </div>
      )}

      {/* ── Chart ── */}
      <div style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.8)",
              zIndex: 10,
              fontSize: 13,
              color: "#888780",
              borderRadius: 8,
            }}
          >
            Loading…
          </div>
        )}

        {error && (
          <div style={{ padding: 16, color: "#A32D2D", fontSize: 13 }}>
            {error}
          </div>
        )}

        {!error && series.length > 0 && (
          <div ref={chartRef}>
            <ReactApexChart
              key={`shift-heatmap-${month}-${year}-${selectedLabId ?? "page" + page}`}
              options={chartOptions}
              series={series}
              type="heatmap"
              height={chartHeight}
            />
          </div>
        )}

        {!loading && !error && series.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#888780",
              fontSize: 13,
            }}
          >
            No data found.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const SEL = {
  fontSize: 12,
  padding: "4px 8px",
  borderRadius: 6,
  border: "0.5px solid #B4B2A9",
  background: "#fff",
  color: "#2C2C2A",
  cursor: "pointer",
  fontFamily: "inherit",
};

const pgBtn = (disabled) => ({
  fontSize: 11,
  padding: "4px 10px",
  borderRadius: 6,
  border: "0.5px solid #B4B2A9",
  background: disabled ? "#F1EFE8" : "#fff",
  color: disabled ? "#B4B2A9" : "#5F5E5A",
  cursor: disabled ? "default" : "pointer",
  fontFamily: "inherit",
});
